import { Component, createRef, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';
import { DocumentTitle } from '../hooks/useDocumentTitle';

/**
 * 🔴 A SCREEN THAT THROWS SAYS SO, AND OFFERS A WAY BACK — design-system plan DS-2a (F198, F58), DS-6a.
 *
 * One exception while a screen drew blanked the whole tree: the till went white mid-sale when a typed price could not
 * be added up, and (DS-6a survey) the back office, portal and console blanked too, or showed the router's unstyled
 * "Something went wrong!". Each shell's root is now inside one of these, and so is the till's sale screen. It keeps
 * the rest of the app alive where it can, says what happened in words, moves focus to that heading so a keyboard or
 * screen-reader user is not left on nothing, and offers Try again (draw afresh — the till's basket is kept in
 * `till/basket.ts`, so nothing is scanned twice) or a full reload.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; title?: string; message?: string },
  { failed: boolean }
> {
  override state = { failed: false };
  private heading = createRef<HTMLHeadingElement>();

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only record there is on a counter; the reference in it is what support asks for.
    console.error('Workbook: a screen failed to draw.', error, info.componentStack);
  }

  // A child that throws on its FIRST draw commits the fallback as a mount, not an update (the till's sale screen,
  // opened with a kept basket): focus the heading then too (DS-6a review).
  override componentDidMount(): void {
    if (this.state.failed) this.heading.current?.focus();
  }

  override componentDidUpdate(_: unknown, before: { failed: boolean }): void {
    if (this.state.failed && !before.failed) this.heading.current?.focus();
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const title = this.props.title ?? 'This screen hit a problem it could not show.';
    return (
      <div className="wb-crash" role="alert">
        {/* DS-11b1: the tab says it too, or it keeps the app's bare name (2.4.2). */}
        <DocumentTitle title={title.replace(/\.$/, '')} />
        <h1 className="wb-crash__title" tabIndex={-1} ref={this.heading}>
          {title}
        </h1>
        <p className="wb-crash__body">{this.props.message ?? 'Nothing you saved is lost. Try again, or reload the page.'}</p>
        <div className="wb-crash__actions">
          <Button variant="primary" onClick={() => this.setState({ failed: false })}>
            Try again
          </Button>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    );
  }
}
